import type { ActionTree } from 'vuex'
import type { CustomerState, LoyaltyProgramTiersMap } from './types'
import { formatActivity, formatCustomerAge, formatCustomerLocation, formatPurchases } from './utils'
import { defaultSelectFields } from '@/api/campaigns'

import type { RootState } from '@/store/modules/types'
import { formatFanName } from '@/store/modules/audience/utils'
import { getContext } from '@/utils/general'
import { fixedEncodeURIComponent } from '@/utils/helpers'

export const customerActions: ActionTree<CustomerState, RootState> = {
  // -------------------
  // Fetches a single Customer
  // -------------------
  async FETCH_CUSTOMER(
    { state, rootState, commit },
    { fanOid = null },
  ) {
    if (state.isFetchingCustomer) return

    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    if (isNaN(fanOid)) return

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMERS', true)

      const customer = await $api.customerProfile.fetch(promoterOid, fanOid)

      // Manually add "name" to Fan Object and reset their data
      const prunedCustomer: any = {
        ...customer,
        name: formatFanName(customer),
        formattedAge: formatCustomerAge(customer),
        formattedLocation: formatCustomerLocation(customer),
        fanActivity: [],
        fanPurchases: [],
        fanCampaigns: [],
        fanEvents: [],
      }

      commit('SET_CUSTOMER', prunedCustomer)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch fan' })
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMERS', false)
    }
  },

  // -------------------
  // Fetches a single Customer custom fields
  // -------------------
  async FETCH_CUSTOMER_CUSTOM_FIELDS(
    { state, rootState, commit },
    { fanOid = null },
  ) {
    if (state.isFetchingCustomerCustomFields) return

    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    if (isNaN(fanOid)) return

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMERS_CUSTOM_FIELDS', true)

      const customerCustomFields = await $api.customerProfile.fetchCustomFields(promoterOid, fanOid)

      commit('SET_CUSTOMER_CUSTOM_FIELDS', customerCustomFields)
    }
    catch (error) {
      console.error(error)
      // $arNotification.push({ type: 'error', message: 'Failed to fetch custom fields for fan' })
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMERS_CUSTOM_FIELDS', false)
    }
  },
  async UPDATE_FAN_CUSTOM_FIELD_DATA({ state, rootState, commit }, { fanOid, customFieldOid, value }) {
    if (!rootState.authModule.account) { return null }
    const promoterOid = rootState.authModule.account.promoterOid

    const { $api, $arNotification } = getContext()

    try {
      const { data, status } = await $api.customFields.updateCustomFieldFanData(
        promoterOid,
        fanOid,
        customFieldOid,
        value,
      )
      const {
        stringValue,
        datetimeValue,
        intValue,
        optionValues,
        promoterCustomFieldOid,
      } = data
      const changes: any = {}
      if (stringValue) {
        changes.customFieldString = {
          [promoterCustomFieldOid]: stringValue,
        }
      }
      if (datetimeValue) {
        changes.customFieldDatetime = {
          [promoterCustomFieldOid]: datetimeValue,
        }
      }
      if (intValue) {
        changes.customFieldInteger = {
          [promoterCustomFieldOid]: intValue,
        }
      }
      if (optionValues) {
        changes.customFieldOptions = {
          [promoterCustomFieldOid]: optionValues,
        }
      }
      commit('UPDATE_CUSTOMER_CUSTOM_FIELDS', changes)
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to update custom field',
      })
      return false
    }
  },
  async DELETE_FAN_CUSTOM_FIELD_DATA({ state, rootState, commit }, { fanOid, customFieldOid }) {
    if (!rootState.authModule.account) { return null }
    const promoterOid = rootState.authModule.account.promoterOid

    const { $api, $arNotification } = getContext()

    try {
      const { data, status } = await $api.customFields.deleteCustomFieldFanData(
        promoterOid,
        fanOid,
        customFieldOid,
      )

      const changes: any = {
        customFieldString: {
          [customFieldOid]: null,
        },
        customFieldDatetime: {
          [customFieldOid]: null,
        },
        customFieldInteger: {
          [customFieldOid]: null,
        },
        customFieldOptions: {
          [customFieldOid]: [],
        },
      }
      commit('UPDATE_CUSTOMER_CUSTOM_FIELDS', changes)
      return data
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to update custom field',
      })
      return false
    }
  },

  /**
   * FETCH_CUSTOMER_TAGS
   */
  async FETCH_CUSTOMER_TAGS({ rootState, commit }, fanOid: number) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    const url = `/promoter/${promoterOid}/tag/${fanOid}/fan`

    const { $axios } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_TAGS', true)
      const { data } = await $axios.get(url, {
        params: {
          $filter: `fanOid=${fanOid} AND promoter_oid=${promoterOid}`,
          $top: 200,
        },
      })
      const tags = data.map((tag: any) => {
        return tag.promoterTag
      })

      commit('SET_CUSTOMER_TAGS', tags)
      return true
    }
    catch (error) {
      console.error(error)
      return false
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_TAGS', false)
    }
  },

  /**
   * FETCH_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS
   * Fetches the message lists that a customer is currently subscribed to
   */
  async FETCH_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS({ rootState, commit }, fanOid: number) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    const url = `/promoter/${promoterOid}/fan-message-list-subscription`

    const { $axios } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS', true)
      const { data } = await $axios.get(url, {
        params: {
          $top: 50,
          $skip: 0,
          $orderby: 'oid desc',
          $count: true,
          $filter: `fanOid=${fanOid} AND userDefined = true AND (email OR sms)`,
          $select: 'messageList,sms,email,facebookMessenger',
        },
      })
      const subscriptions = data.rows || []
      commit('SET_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS', subscriptions)
      return subscriptions
    }
    catch (error) {
      console.error(error)
      return []
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS', false)
    }
  },

  // -------------------
  // Fetches the activity for a single customer
  // -------------------
  async FETCH_CUSTOMER_ACTIVITY(
    { state, rootState, commit },
    { top = 50, skip = 0, types = null, notTypes = null, append = false, orderby = 'activityTime desc', fanOid = null },
  ) {
    if (state.isFetchingActivity) return

    if (!rootState.authModule.account) return
    const { promoterOid } = rootState.authModule.account
    if (!state.customer) return

    if (state.hasFetchCustomerActivityFailed) {
      console.error(`FETCH_CUSTOMER_ACTIVITY cancelled due to previous failed request.`)
      return
    }

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_ACTIVITY', true)

      if (!append) commit('SET_IS_NO_MORE_CUSTOMER_ACTIVITY', false)

      const uri = `/promoter/${promoterOid}/fan-activity`

      if (fanOid == null) {
        fanOid = state.customer.oid
      }

      let filterString = `fanOid=${fanOid}`
      let notTypesFilter = null
      if (notTypes && notTypes.length > 0) {
        const joinString = ` AND activityType != `
        notTypesFilter = `activityType != ${notTypes.join(joinString)}`
        filterString += ` AND ${notTypesFilter}`
      }
      let typesFilter = null
      if (types && types.length > 0) {
        const joinString = ` OR fanOid=${fanOid} AND activityType = `
        typesFilter = `activityType = ${types.join(joinString)}`
        filterString += ` AND ${typesFilter}`
      }

      const { data } = await $axios.get(uri, {
        params: {
          $top: top,
          $count: true,
          $skip: skip,
          $orderby: orderby,
          $filter: filterString,
        },
      })

      const activityList = formatActivity(data.rows, state.customerModule)
      const rawActivityList = data.rows // Store raw server data

      if (append) {
        commit('CONCAT_CUSTOMER_ACTIVITY', activityList)
        commit('CONCAT_RAW_CUSTOMER_ACTIVITY', rawActivityList)
      }
      else {
        commit('REPLACE_CUSTOMER_ACTIVITY', activityList)
        commit('REPLACE_RAW_CUSTOMER_ACTIVITY', rawActivityList)
      }

      if (state.fanActivity?.length >= data.count) {
        commit('SET_IS_NO_MORE_CUSTOMER_ACTIVITY', true)
      }

      // If we're waiting on activity we know is coming (e.g. a just-saved subscription edit), check whether
      // any row not present in the baseline snapshot has shown up yet - the backend can take a while to
      // process these, so this is what clears the loading placeholders once the real data lands.
      if (state.pendingActivityCount > 0) {
        const hasNewRow = rawActivityList.some((row: any) => !state.pendingActivityBaselineOids.includes(row?.oid))
        if (hasNewRow) {
          commit('CLEAR_PENDING_ACTIVITY')
        }
      }
    }
    catch (e) {
      console.error(e)
      $arNotification.push({ type: 'error', message: 'Failed to fetch fan activity' })
      commit('SET_HAS_FETCH_CUSTOMER_ACTIVITY_FAILED', true)
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_ACTIVITY', false)
    }
  },

  // -------------------
  // Fetches the events for a single customer
  // We do this by explicitly fetching using event oids provided by customer
  // -------------------
  async FETCH_CUSTOMER_EVENTS(
    { state, rootState, commit, dispatch },
    { searchString = null, append = false, dateRange = null },
  ) {
    if (!state.customer) return
    if (state.isFetchingEvents) {
      console.warn('FETCH_CUSTOMER_EVENTS: Already fetching, skipping...')
      return
    }
    if (state.hasFetchCustomerEventsFailed) {
      console.error(`FETCH_CUSTOMER_EVENTS cancelled due to previous failed request.`)
      return
    }

    // We get all the event oids from customer, sort them by purchaseDate, filter out the ones which
    // exist in actionedEventOids and then return an array
    const unfetchedEventOidsOrdered = state.customer.events.map((event) => {
      return {
        oid: event,
        date: state.customer && state.customer.purchaseDatePerEvent && state.customer.purchaseDatePerEvent[event] ? state.customer.purchaseDatePerEvent[event] : null,
      }
    }).sort((a, b) => {
      if (a.date && b.date && a.date > b.date) return -1
      if (a.date && b.date && a.date < b.date) return 1
      return 0
    }).filter(event => !state.actionedEventOids.includes(event.oid),
    ).map(event => event.oid)

    const oidsToFetch = []
    while (oidsToFetch.length < 10 && unfetchedEventOidsOrdered.length > 0) {
      oidsToFetch.push(unfetchedEventOidsOrdered.shift())
    }

    if (oidsToFetch.length === 0) return
    let fetchedEvents = []
    let error = false

    try {
      commit('SET_IS_FETCHING_CUSTOMER_EVENTS', true)
      fetchedEvents = await dispatch('FETCH_EVENTS_BY_OID_AND_SEARCH', { oids: oidsToFetch, searchString, dateRange })
      if (append) {
        commit('CONCAT_CUSTOMER_EVENTS', fetchedEvents)
      }
      else {
        await commit('RESET_CUSTOMER_EVENTS')
        commit('CONCAT_CUSTOMER_EVENTS', fetchedEvents)
      }
      commit('CONCAT_CUSTOMER_ACTIONED_EVENT_OIDS', oidsToFetch)
    }
    catch (e) {
      error = true
      this.$arNotification.push({ type: 'error', message: 'Failed to fetch fan events' })
      commit('SET_HAS_FETCH_CUSTOMER_EVENTS_FAILED', true)
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_EVENTS', false)
      if (
        searchString
        && fetchedEvents.length < 5
        && unfetchedEventOidsOrdered.length > 0
        && state.actionedEventOids.length < state.customer.events.length
        && !error) {
        dispatch('FETCH_CUSTOMER_EVENTS', { searchString, append: true, dateRange })
      }
    }
  },

  /**
  * @function FETCH_CUSTOMER_MESSAGE_SUMMARY
  * Fetches the message summary for a single customer by oid
  *
  **/
  async FETCH_CUSTOMER_MESSAGE_SUMMARY(
    { state, rootState, commit },
    fanOid: number,
  ) {
    if (!fanOid) return null
    if (!rootState.authModule.account) return null
    const { promoterOid } = rootState.authModule.account

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_MESSAGE_SUMMARY', true)

      const { data } = await $axios.get(`/promoter/${promoterOid}/fan-profile-snapshot?$filter=fan-oid=${fanOid}`)

      commit('SET_CUSTOMER_MESSAGE_SUMMARY', data)
      return data
    }
    catch (error) {
      console.error('Failed to fetch customer message summary:', error)
      // commenting this error out until we have this API endpoint in server
      // https://bitbucket.org/audiencerepublic/server/pull-requests/3328
      // $arNotification.push({
      //   type: 'error',
      //   message: 'Failed to fetch messaging summary',
      // })
      return null
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_MESSAGE_SUMMARY', false)
    }
  },

  /**
  * @function FETCH_CUSTOMER_MESSAGE_TOTALS
  * Fetches all-time, channel-combined messaging totals from MFPAM. Used to
  * surface a total count alongside the windowed snapshot — see EC-3953.
  **/
  async FETCH_CUSTOMER_MESSAGE_TOTALS(
    { rootState, commit },
    fanOid: number,
  ) {
    if (!fanOid) return null
    if (!rootState.authModule.account) return null
    const { promoterOid } = rootState.authModule.account

    const { $axios } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_MESSAGE_TOTALS', true)

      const { data } = await $axios.get(`/promoter/${promoterOid}/materialized-fan-messaging/${fanOid}`)

      commit('SET_CUSTOMER_MESSAGE_TOTALS', data)
      return data
    }
    catch (error) {
      console.error('Failed to fetch customer message totals:', error)
      return null
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_MESSAGE_TOTALS', false)
    }
  },

  // -------------------
  // Fetches the campaigns for a single customer
  // We do this by explicitly fetching using campaign oids provided by customer
  // -------------------
  async FETCH_CUSTOMER_CAMPAIGNS(
    { state, rootState, commit, dispatch },
    { searchString = null, append = false, orderby = 'startDate desc', top = 50, skip = undefined },
  ) {
    if (!state.customer) return
    if (!rootState.authModule.account) return
    if (state.isFetchingCampaigns) return
    if (state.hasFetchCustomerCampaignsFailed) {
      if (append) {
        console.error(`FETCH_CUSTOMER_CAMPAIGNS cancelled due to previous failed request.`)
        return
      }
      commit('SET_HAS_FETCH_CUSTOMER_CAMPAIGNS_FAILED', false)
    }

    let fetchedCampaigns = []
    const { $axios, $arNotification } = getContext()
    const { promoterOid } = rootState.authModule.account
    const customerOid = state.customer.oid

    try {
      commit('SET_IS_FETCHING_CUSTOMER_CAMPAIGNS', true)

      const { data } = await $axios.post(
        `/promoter/${promoterOid}/materialized-fan/${customerOid}/data-capture`,
        {
          $orderby: orderby,
          $search: searchString,
          $skip: typeof skip === 'number' ? skip : (append ? state.actionedCampaignOids.length : 0),
          $top: top,
        },
      )
      if (state.customer?.oid !== customerOid) return

      const oidsToFetch = data.rows.map(campaign => campaign.oid)
      fetchedCampaigns = await dispatch('FETCH_CAMPAIGNS_BY_OID_AND_SEARCH', { oids: oidsToFetch })
      if (state.customer?.oid !== customerOid) return
      if (fetchedCampaigns.length !== oidsToFetch.length) {
        throw new Error('Failed to fetch the complete campaign page')
      }
      const campaignsByOid = new Map(fetchedCampaigns.map(campaign => [campaign.oid, campaign]))
      fetchedCampaigns = oidsToFetch.flatMap((oid) => {
        const campaign = campaignsByOid.get(oid)
        return campaign ? [campaign] : []
      })

      if (append) {
        commit('CONCAT_CUSTOMER_CAMPAIGNS', fetchedCampaigns)
      }
      else {
        commit('RESET_CUSTOMER_CAMPAIGNS')
        commit('CONCAT_CUSTOMER_CAMPAIGNS', fetchedCampaigns)
      }
      commit('CONCAT_CUSTOMER_ACTIONED_CAMPAIGN_OIDS', oidsToFetch)
      commit('SET_CUSTOMER_CAMPAIGN_COUNT', data.count)
    }
    catch {
      $arNotification.push({ type: 'error', message: 'Failed to fetch fan campaigns' })
      commit('SET_HAS_FETCH_CUSTOMER_CAMPAIGNS_FAILED', true)
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_CAMPAIGNS', false)
    }
  },

  // -------------------
  // Fetches the purchases for a single customer
  // -------------------
  async FETCH_CUSTOMER_PURCHASES(
    { state, rootState, commit },
    { top = 50, append = false, searchString = null, orderby = 'oid desc', fanOid = null, skip = undefined },
  ) {
    if (state.isFetchingPurchases) return
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account
    if (!state.customer) return
    if (state.hasFetchCustomerPurchasesFailed) {
      console.error(`FETCH_CUSTOMER_PURCHASES cancelled due to previous failed request.`)
      return
    }

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_PURCHASES', true)

      // let uri = `/promoter/${promoterOid}/fan-profile-ticket-sales`;
      const uri = `/promoter/${promoterOid}/fan-profile-ticket-sales`
      if (fanOid == null) {
        fanOid = state.customer.oid
      }

      let filterString = `fanOid=${fanOid}`
      let typesFilter = null
      if (searchString) {
        typesFilter = ` AND event[name] ILIKE "%${searchString}%"`
        typesFilter += ` OR fanOid=${fanOid} AND campaign[name] ILIKE "%${searchString}%"`
        typesFilter += ` OR fanOid=${fanOid} AND campaign[type] ILIKE "%${searchString}%"`
        typesFilter += ` OR fanOid=${fanOid} AND orderId ILIKE "%${searchString}%"`
        typesFilter += ` OR fanOid=${fanOid} AND status ILIKE "%${searchString}%"`
        typesFilter += ` OR fanOid=${fanOid} AND type ILIKE "%${searchString}%"`
        typesFilter += ` OR fanOid=${fanOid} AND provider ILIKE "%${searchString}%"`

        filterString += typesFilter
      }

      // `skip` is opt-in: existing infinite-scroll callers never pass it and keep
      // deriving the offset from the currently-loaded length via `append`.
      const effectiveSkip = typeof skip === 'number' ? skip : (append ? state.fanPurchases.length : 0)

      const { data } = await $axios.get(uri, {
        params: {
          $top: top,
          $count: true,
          $skip: effectiveSkip,
          $orderby: orderby,
          $filter: filterString,
        },
      })

      const formattedPurchases = formatPurchases(data.rows)

      if (append) {
        commit('CONCAT_CUSTOMER_PURCHASES', formattedPurchases)
      }
      else {
        await commit('RESET_CUSTOMER_PURCHASES')
        commit('CONCAT_CUSTOMER_PURCHASES', formattedPurchases)
      }

      commit('SET_TOTAL_PURCHASES_COUNT', data.count || 0)

      if (state.fanPurchases.length >= data.count) {
        commit('SET_IS_NO_MORE_CUSTOMER_PURCHASES', true)
      }
    }
    catch (e) {
      console.error(e)
      $arNotification.push({ type: 'error', message: 'Failed to fetch fan purchases' })
      commit('SET_HAS_FETCH_CUSTOMER_PURCHASES_FAILED', true)
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_PURCHASES', false)
    }
  },

  async FETCH_CUSTOMER_LOYALTY_MEMBERSHIPS(
    { state, rootState, commit },
    fanOid: number,
  ) {
    if (state.isFetchingLoyaltyMemberships) return
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_LOYALTY_MEMBERSHIPS', true)

      const fanLoyaltyMemberships = await $api.customerProfile.fetchFanLoyaltyMembership(promoterOid, fanOid)
      commit('SET_CUSTOMER_LOYALTY_MEMBERSHIPS', fanLoyaltyMemberships)

      if (fanLoyaltyMemberships.length > 0) {
        const loyaltyProgramTierOids = fanLoyaltyMemberships.map((item: any) => item.fanLoyaltyTier.loyaltyTierOid).filter((item: any) => !!item)
        const loyaltyProgramTiers = await $api.customerProfile.fetchLoyaltyProgramTiersByOids(promoterOid, loyaltyProgramTierOids)
        const loyaltyProgramTiersMap: LoyaltyProgramTiersMap = {}
        loyaltyProgramTiers.forEach((tier: any) => {
          loyaltyProgramTiersMap[tier.oid] = tier
        })
        commit('SET_CUSTOMER_LOYALTY_PROGRAM_TIERS_MAP', loyaltyProgramTiersMap)
      }
    }
    catch (e) {
      console.error(e)
      $arNotification.push({ type: 'error', message: 'Failed to fetch active memberships' })
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_LOYALTY_MEMBERSHIPS', false)
    }
  },

  async FETCH_CUSTOMER_LOYALTY_PROGRAMS(
    { state, rootState, commit },
  ) {
    if (state.isFetchingLoyaltyPrograms) return
    if (!rootState.authModule.account) { return }
    const { promoterOid } = rootState.authModule.account

    const { $api, $arNotification } = getContext()

    try {
      commit('SET_IS_FETCHING_CUSTOMER_LOYALTY_PROGRAMS', true)

      const loyaltyPrograms = await $api.customerProfile.fetchPromoterIntegrationLoyaltyPrograms(promoterOid)
      commit('SET_CUSTOMER_LOYALTY_PROGRAMS', loyaltyPrograms)
    }
    catch (e) {
      console.error(e)
      // $arNotification.push({ type: 'error', message: 'Failed to fetch active loyalty programs' })
    }
    finally {
      commit('SET_IS_FETCHING_CUSTOMER_LOYALTY_PROGRAMS', false)
    }
  },

  async FETCH_EVENTS_BY_OID_AND_SEARCH({ rootState, state, commit }, { oids, searchString, dateRange = null }) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    if (!oids || oids.length === 0) {
      return []
    }

    let url = `/promoter/${promoterOid}/event?$select=name,description,campaigns,meta,capacity,location,startDate,endDate,timeZone,tourOid,presentation,resources,ticket-stats,paymentInfo&$top=all&$orderby=oid%20desc&$filter=`

    const oidsRequest = `oid=${oids.join(' OR oid=')}`
    // Wrap OIDs in parentheses if we have search string or date range filters
    const needsParentheses = searchString || dateRange
    url += needsParentheses ? `%28${fixedEncodeURIComponent(oidsRequest)}%29` : fixedEncodeURIComponent(oidsRequest)

    if (searchString && searchString.length > 0) {
      const encodedComponent = fixedEncodeURIComponent(`name ILIKE "%${searchString}%"`)
      url += `${fixedEncodeURIComponent(' AND ')}%28${encodedComponent}%29`
    }

    // Add date range filtering if provided
    if (dateRange) {
      if (dateRange.startDate) {
        // Subtract 1 day from start date so > works as >= for user
        const adjustedStartDate = new Date(dateRange.startDate)
        adjustedStartDate.setDate(adjustedStartDate.getDate() - 1)
        const startDateStr = adjustedStartDate.toISOString().split('T')[0]
        const dateFilterStart = fixedEncodeURIComponent(`startDate > '${startDateStr}'`)
        url += `${fixedEncodeURIComponent(' AND ')}${dateFilterStart}`
      }
      if (dateRange.endDate) {
        // Add 1 day to end date so < works as <= for user
        const adjustedEndDate = new Date(dateRange.endDate)
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1)
        const endDateStr = adjustedEndDate.toISOString().split('T')[0]
        const dateFilterEnd = fixedEncodeURIComponent(`startDate < '${endDateStr}'`)
        url += `${fixedEncodeURIComponent(' AND ')}${dateFilterEnd}`
      }
    }

    const { $axios } = getContext()

    try {
      const { data } = await $axios.get(url)

      return data
    }
    catch (error) {
      console.error(error)
      return []
    }
  },

  async FETCH_CAMPAIGNS_BY_OID_AND_SEARCH({ rootState, state, commit }, { oids, searchString, allowOptIns = false }) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account

    if (!oids || oids.length === 0) {
      return []
    }

    const url = `/promoter/${promoterOid}/campaign`
    let filterString = ''
    if (searchString && searchString.length > 0) {
      filterString += `name ILIKE "%${searchString}%" AND `
    }
    if (!allowOptIns) {
      filterString += `type != opt-in AND type != rsvp AND `
    }
    const oidsRequest = `oid=${oids.join(' OR oid=')}`
    filterString += `(${oidsRequest})`

    const params = {
      $select: defaultSelectFields,
      $top: 'all',
      $orderby: 'oid desc',
      $filter: filterString,
    }

    const { $axios } = getContext()

    try {
      const { data } = await $axios.get(url, { params })
      return data
    }
    catch (error) {
      return []
    }
  },

}
